import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import {
  DialogResultTypes,
  HowdenAlertService,
  HowdenAskForDataService,
  HowdenTableComponent,
  IButtonActionData,
  IHowdenAskForData,
  IHowdenYesNoData,
  YesNoService
} from '@howdeniberia/core-front';
import { IDeleteCredentialDto } from 'src/app/core/models/api/delete-credential-dto';
import { ClavesCiasApiService } from 'src/app/core/services/api/claves-cias-api.service';
import { ClavesCiasContract } from 'src/app/core/services/api/claves-cias-contract';
import { CredentialRowModel } from 'src/app/features/credentials/models/ui/credential-row.model';
import { CredentialViewModel } from 'src/app/features/credentials/models/ui/credential-view-model';
import { ShowEditCredentialService } from 'src/app/features/credentials/services/show-edit-credential.service';
import { ICredentialDto } from './../../../../core/models/api/credential-dto';
import { CredentialsMapper } from './../../mappers/credential-mapper';

@Component({
  selector: 'howden-credentials-list',
  templateUrl: './credentials-list.component.html',
  styleUrls: ['./credentials-list.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSlideToggle,
    HowdenTableComponent
  ],
  providers: [
    { provide: ClavesCiasContract, useClass: ClavesCiasApiService }
  ]
})
export class CredentialsListComponent implements OnInit {
  private askForDataSrv = inject(HowdenAskForDataService);
  private clavesCiasSrv = inject(ClavesCiasContract);
  private showEditSrv = inject(ShowEditCredentialService);
  private yesNoSrv = inject(YesNoService);
  private alertSrv = inject(HowdenAlertService);
  private clipBoardSrv = inject(Clipboard);
  private mapper = new CredentialsMapper();

  @ViewChild(HowdenTableComponent) table!: HowdenTableComponent;
  pageSize = 10;
  model: CredentialRowModel = new CredentialRowModel();
  dataRaw: CredentialViewModel[] = [];
  data: CredentialViewModel[] = [];

  readonly C_DELETED = 'filt_deleted';

  form: FormGroup = new FormGroup({
    [this.C_DELETED]: new FormControl(false)
  });

  ngOnInit(): void {
    this.reload();
  }

  public reload(): void {
    this.clavesCiasSrv.getCredentials().subscribe({
      next: (apiData) => {
        this.dataRaw = this.mapper.credentialDtoArrayToViewModelArray(apiData);
        this.filterData();
      }
    });
  }

  filterData(): void {
    const showDeleted = this.form.get(this.C_DELETED)?.value;
    const filtered = showDeleted
      ? this.dataRaw.filter(x => x.deleted === true)
      : this.dataRaw.filter(x => x.deleted === false);
    this.data = filtered;
  }

  buttonClick(data: IButtonActionData): void {
    switch (data.buttonName) {
      case this.model.BTN_DELETE:
        this.deleteCredential(data.row);
        break;
      case this.model.BTN_EDIT:
        this.editCredential(data.row);
        break;
      case this.model.BTN_COPY_PWD:
        this.copyPassword(data.row);
        break;
      case this.model.BTN_COPY_USR:
        this.copyUser(data.row);
        break;
      case this.model.BTN_REACTIVATE:
        this.reactivateCredential(data.row);
        break;
    }
  }

  editCredential(data: CredentialViewModel): void {
    this.showEditSrv.open(data).subscribe(result => {
      if (result) {
        this.clavesCiasSrv.updateCredential(this.mapper.viewModelToCredentialDto(result)).subscribe({
          next: (updatedRow) => {
            this.updateOnDataRaw(updatedRow);
            this.alertSrv.info(`Credenciales actualizadas para ${updatedRow.company}`, '');
          }
        });
      }
    });
  }

  deleteCredential(data: CredentialViewModel): void {
    const options = {
      title: `Seguro de eliminar la credencial ${data.company}`,
      allowCancel: false,
      cancelActionText: 'mejor no',
      okActionText: 'si seguro'
    } as IHowdenYesNoData;
    this.yesNoSrv.open(options).subscribe(result => {
      if (result) {
        const options2 = { title: 'Debes indicar un motivo' } as IHowdenAskForData;
        this.askForDataSrv.open(options2).subscribe({
          next: (reason) => {
            if (reason.result === DialogResultTypes.OK) {
              const deleteObj = {
                credentialId: data.credentialId,
                reasonToDelete: reason.resultData
              } as IDeleteCredentialDto;
              this.clavesCiasSrv.deleteCredential(deleteObj).subscribe({
                next: (deleteResult) => {
                  this.updateOnDataRaw(deleteResult);
                  this.alertSrv.success(`eliminada! ${data.company}`, '');
                }
              });
            }
          }
        });
      }
    });
  }

  reactivateCredential(data: CredentialViewModel): void {
    if (!data.deleted) {
      this.alertSrv.info('Credencial ya activa!', '');
      return;
    }
    const options = {
      title: `Seguro de re activar la credencial ${data.company}`,
      allowCancel: false,
      cancelActionText: 'mejor no',
      okActionText: 'si seguro'
    } as IHowdenYesNoData;
    this.yesNoSrv.open(options).subscribe(result => {
      if (result) {
        this.clavesCiasSrv.reactivateCredential(data.credentialId).subscribe({
          next: (reactivateResult) => {
            if (reactivateResult) {
              data.deleted = false;
              this.updateOnDataRaw(this.mapper.viewModelToCredentialDto(data));
              this.alertSrv.success(`reactivada! ${data.company}`, '');
            }
          }
        });
      }
    });
  }

  public updateOnDataRaw(credentialDto: ICredentialDto): void {
    const index = this.dataRaw.findIndex(x => x.credentialId === credentialDto.credentialId);
    this.dataRaw[index] = this.mapper.credentialDtoToViewModel(credentialDto);
    this.filterData();
  }

  public insertOnDataraw(credentialDto: ICredentialDto): void {
    const index = this.dataRaw.findIndex(x => x.credentialId === credentialDto.credentialId);
    if (index > -1) {
      return;
    }
    this.dataRaw.push(this.mapper.credentialDtoToViewModel(credentialDto));
    this.filterData();
  }

  copyUser(data: CredentialViewModel): void {
    this.clipBoardSrv.copy(data.user);
    this.alertSrv.info('Usuario copiado!', '');
  }

  copyPassword(data: CredentialViewModel): void {
    this.clipBoardSrv.copy(data.password);
    this.alertSrv.info('Contraseña copiada!', '');
  }
}
